import React, { useState, useRef, useEffect, useCallback } from 'react';
import Button from './Button';
import { useError } from '../contexts/ErrorContext';
import { getAvailableFirmwareChannels, getFirmwareChannel } from '../services/storageService';
import {
  trackOTAFlashStarted,
  trackOTAFlashCompleted,
  trackOTAFlashFailed
} from '../services/analytics';
import {
  VARIANTS,
  VARIANT_LABELS,
  VARIANT_SHORT_LABELS,
  connectAndDetect,
  closeTransport,
  closePort,
  loadFirmwareImages,
  flashImages,
  listenForBoot,
  rebootIntoApp,
  supportsBootLine,
  formatVersion,
  formatChannelName,
  compareManifestVersions
} from '../services/espFlasher';
import useSerialPortWatcher from '../hooks/useSerialPortWatcher';
import './FactoryFlashMode.css';

// Browser-only memory of the last variant -> channel/version mapping, so a
// reloaded station comes back with its setup filled in.
const MAPPING_STORAGE_KEY = 'factoryFlash.lastMapping';

// After flashing, every goggle is rebooted into its new firmware (as OTA
// Management does). Its USB port usually drops and comes back as the chip
// restarts, and the page must not mistake that for a new goggle.
// RESTART_WAIT_MS: how long a restarting goggle is waited for.
// BOOT_LISTEN_MS: how long each newly appearing port is read, without touching
//   the chip, for boot output before it is treated as a new goggle.
// BANNER_MATCH_MS: an app boot banner with no MAC is only attributed to a
//   restarting goggle whose own port dropped within this long.
const RESTART_WAIT_MS = 20000;
const BOOT_LISTEN_MS = 3000;
const BANNER_MATCH_MS = 10000;

// How long a card stays after its goggle is unplugged.
const CARD_LINGER_MS = 3000;

const IN_PROGRESS_STATES = new Set(['checking', 'detecting', 'erasing', 'flashing', 'restarting']);

const readStoredMapping = () => {
  try {
    return JSON.parse(localStorage.getItem(MAPPING_STORAGE_KEY)) || {};
  } catch (e) {
    return {};
  }
};

const writeStoredMapping = (rows) => {
  try {
    const mapping = {};
    VARIANTS.forEach(v => {
      const row = rows[v];
      mapping[v] = {
        enabled: row.enabled,
        channel: row.channel,
        version: row.manifest ? formatVersion(row.manifest.manifestFirmware) : null
      };
    });
    localStorage.setItem(MAPPING_STORAGE_KEY, JSON.stringify(mapping));
  } catch (e) {
    // Storage blocked; the mapping just won't survive a reload.
  }
};

const emptyRow = () => ({ enabled: false, channel: '', manifests: [], manifest: null, loading: false });

const formatClock = (date) => date.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });

/**
 * Factory Flash page body (src/pages/FactoryFlashPage.jsx): pick a firmware build per
 * hardware variant, press Start, and every goggle plugged in afterwards is
 * detected and flashed with no further clicks. One card per plug-in.
 *
 * @param {{addLog: (message: string, type?: string) => void,
 *          onRunningChange?: (running: boolean) => void}} props
 */
const FactoryFlashMode = ({ addLog, onRunningChange }) => {
  const { addError } = useError();

  // --- Setup -------------------------------------------------------------
  const [channels, setChannels] = useState([]);
  const [isLoadingChannels, setIsLoadingChannels] = useState(true);
  const [rows, setRows] = useState(() => {
    const initial = {};
    VARIANTS.forEach(v => { initial[v] = emptyRow(); });
    return initial;
  });
  const storedMappingRef = useRef(readStoredMapping());

  // --- Run ---------------------------------------------------------------
  // setup -> preparing (downloading firmware) -> running -> stopping -> setup
  const [phase, setPhase] = useState('setup');
  const [runStartedAt, setRunStartedAt] = useState(null);
  const [counts, setCounts] = useState({ flashed: 0, errors: 0 });
  const [slots, setSlots] = useState([]);
  const [hiddenAt, setHiddenAt] = useState(null);

  // Firmware pinned at Start, per enabled variant:
  // { channel, manifest, version, images, bootLine }
  const pinnedRef = useRef({});
  // Per-slot mutable data the async flash pipeline reads:
  // id -> { port, transport, gone, state, mac, variant, removeTimer }
  const slotMetaRef = useRef(new Map());
  const portToSlotRef = useRef(new Map());
  // Goggles rebooted after flashing, not yet seen coming back:
  // slotId -> { mac, timer, droppedAt (ms, when its port dropped, or null) }
  const awaitingBootRef = useRef(new Map());
  const nextSlotNumberRef = useRef(1);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const wakeLockRef = useRef(null);

  useEffect(() => {
    onRunningChange?.(phase !== 'setup');
  }, [phase, onRunningChange]);

  const updateRow = useCallback((variant, patch) => {
    setRows(prev => {
      const next = { ...prev, [variant]: { ...prev[variant], ...patch } };
      writeStoredMapping(next);
      return next;
    });
  }, []);

  const selectChannel = useCallback(async (variant, channel, preferredVersion = null) => {
    updateRow(variant, { channel, manifests: [], manifest: null, loading: !!channel });
    if (!channel) return;
    try {
      const data = await getFirmwareChannel(channel, variant);
      const manifests = [...(data?.manifests || [])].sort(compareManifestVersions);
      const preferred = preferredVersion
        ? manifests.find(m => formatVersion(m.manifestFirmware) === preferredVersion)
        : null;
      updateRow(variant, { manifests, manifest: preferred || manifests[0] || null, loading: false });
    } catch (error) {
      updateRow(variant, { loading: false });
      addError(`Failed to load ${channel} channel`, error.message, 'API');
    }
  }, [updateRow, addError]);

  // Load the channel list once, then restore the stored mapping.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getAvailableFirmwareChannels();
        if (cancelled) return;
        const list = data?.channels || [];
        setChannels(list);
        const stored = storedMappingRef.current;
        VARIANTS.forEach(v => {
          const saved = stored[v];
          if (!saved) return;
          setRows(prev => ({ ...prev, [v]: { ...prev[v], enabled: !!saved.enabled } }));
          if (saved.channel && list.includes(saved.channel)) {
            selectChannel(v, saved.channel, saved.version);
          }
        });
      } catch (error) {
        if (!cancelled) addError('Failed to load firmware channels', error.message, 'API');
      } finally {
        if (!cancelled) setIsLoadingChannels(false);
      }
    })();
    return () => { cancelled = true; };
    // Runs once on mount; selectChannel and addError are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enabledVariants = VARIANTS.filter(v => rows[v].enabled);
  const canStart = phase === 'setup'
    && enabledVariants.length > 0
    && enabledVariants.every(v => rows[v].manifest && !rows[v].loading);

  // --- Slots -------------------------------------------------------------

  const patchSlot = useCallback((id, patch) => {
    const meta = slotMetaRef.current.get(id);
    if (meta && patch.state) meta.state = patch.state;
    setSlots(prev => prev.map(s => (s.id === id ? { ...s, ...patch } : s)));
  }, []);

  const removeSlot = useCallback((id) => {
    const meta = slotMetaRef.current.get(id);
    if (meta?.removeTimer) clearTimeout(meta.removeTimer);
    slotMetaRef.current.delete(id);
    setSlots(prev => prev.filter(s => s.id !== id));
  }, []);

  const scheduleRemoval = useCallback((id) => {
    const meta = slotMetaRef.current.get(id);
    if (!meta) return;
    if (meta.removeTimer) clearTimeout(meta.removeTimer);
    meta.removeTimer = setTimeout(() => removeSlot(id), CARD_LINGER_MS);
  }, [removeSlot]);

  const slotLog = useCallback((id) => (message, type = 'info') => {
    const meta = slotMetaRef.current.get(id);
    const tag = meta?.mac || meta?.label || id;
    addLog(`[${tag}] ${message}`, type);
  }, [addLog]);

  // A goggle reached its final state. Card stays until the goggle is unplugged.
  const finishSlot = useCallback((id, patch) => {
    patchSlot(id, patch);
    const meta = slotMetaRef.current.get(id);
    if (meta?.gone) scheduleRemoval(id);
  }, [patchSlot, scheduleRemoval]);

  const failSlot = useCallback((id, message) => {
    const meta = slotMetaRef.current.get(id);
    const pinned = meta?.variant ? pinnedRef.current[meta.variant] : null;
    slotLog(id)(`Error: ${message}`, 'error');
    setCounts(c => ({ ...c, errors: c.errors + 1 }));
    if (pinned) {
      trackOTAFlashFailed({
        channel: pinned.channel,
        firmwareVersion: pinned.version,
        deviceMac: meta?.mac,
        error: message
      });
    }
    finishSlot(id, { state: 'error', error: message, finishedAt: Date.now() });
  }, [slotLog, finishSlot]);

  // outcome: 'verified' (REKKIE-BOOT line matched MAC and version),
  // 'restarted' (app boot banner seen after its port dropped),
  // 'flashed' (flash succeeded; restart not observed).
  const completeSlot = useCallback((id, outcome) => {
    const meta = slotMetaRef.current.get(id);
    const pinned = pinnedRef.current[meta.variant];
    const messages = {
      verified: `Booted ${pinned.version}. Verified. Unplug.`,
      restarted: 'Restarted into the new firmware. Unplug.',
      flashed: 'Flash complete. Unplug.'
    };
    slotLog(id)(messages[outcome], 'success');
    setCounts(c => ({ ...c, flashed: c.flashed + 1 }));
    trackOTAFlashCompleted({ channel: pinned.channel, firmwareVersion: pinned.version, deviceMac: meta.mac });
    finishSlot(id, { state: 'done', outcome, progress: 100, finishedAt: Date.now() });
  }, [slotLog, finishSlot]);

  // Stop waiting for a restarting goggle and finish its card.
  const settleRestart = useCallback((slotId, outcome) => {
    const waiting = awaitingBootRef.current.get(slotId);
    if (!waiting) return false;
    clearTimeout(waiting.timer);
    awaitingBootRef.current.delete(slotId);
    completeSlot(slotId, outcome);
    return true;
  }, [completeSlot]);

  // Which restarting goggle, if any, produced this boot output. The awaiting
  // entry is consumed, so a later replug of the same goggle flashes it again.
  //  - REKKIE-BOOT line: exact, by MAC (and pinned version).
  //  - Banner only: the goggle that has been waiting longest since its port
  //    dropped, within BANNER_MATCH_MS. Banner matching is turned off for a
  //    goggle whose build prints REKKIE-BOOT, since that line will follow.
  // Returns the matched slot id, else null.
  const matchRestart = useCallback(({ boot, banner }) => {
    if (boot) {
      for (const [slotId, waiting] of awaitingBootRef.current) {
        if (waiting.mac !== boot.mac) continue;
        const meta = slotMetaRef.current.get(slotId);
        const pinned = meta ? pinnedRef.current[meta.variant] : null;
        if (!pinned || boot.version !== pinned.version) return null;
        settleRestart(slotId, 'verified');
        return slotId;
      }
      return null;
    }
    if (!banner) return null;
    const now = Date.now();
    let best = null;
    for (const [slotId, waiting] of awaitingBootRef.current) {
      const meta = slotMetaRef.current.get(slotId);
      if (meta && pinnedRef.current[meta.variant]?.bootLine) continue;
      if (waiting.droppedAt === null || now - waiting.droppedAt > BANNER_MATCH_MS) continue;
      if (!best || waiting.droppedAt < best.droppedAt) best = { slotId, droppedAt: waiting.droppedAt };
    }
    if (!best) return null;
    settleRestart(best.slotId, 'restarted');
    return best.slotId;
  }, [settleRestart]);

  // Hand a port that turned out to be a restarted goggle to that goggle's
  // original card, so unplugging it later clears the right card.
  const adoptPort = useCallback((tempId, ownerId, port, transport = null) => {
    const owner = slotMetaRef.current.get(ownerId);
    portToSlotRef.current.set(port, ownerId);
    if (owner) {
      owner.port = port;
      owner.transport = transport;
      owner.gone = false;
      if (owner.removeTimer) clearTimeout(owner.removeTimer);
    }
    removeSlot(tempId);
  }, [removeSlot]);

  // Reboot a freshly flashed goggle into its new firmware and wait for it to
  // come back, on the same port or on the port it re-enumerates as.
  const restartSlot = useCallback(async (id) => {
    const meta = slotMetaRef.current.get(id);
    const pinned = pinnedRef.current[meta.variant];
    patchSlot(id, { state: 'restarting' });
    const log = slotLog(id);
    log('Restarting goggles into the new firmware...', 'info');

    const timer = setTimeout(() => {
      if (!awaitingBootRef.current.has(id)) return;
      if (pinned.bootLine) {
        awaitingBootRef.current.delete(id);
        failSlot(id, `No boot confirmation within ${RESTART_WAIT_MS / 1000} s`);
      } else {
        log(`Restart not seen within ${RESTART_WAIT_MS / 1000} s.`, 'warning');
        settleRestart(id, 'flashed');
      }
    }, RESTART_WAIT_MS);
    awaitingBootRef.current.set(id, { mac: meta.mac, timer, droppedAt: null });

    const sent = await rebootIntoApp(meta.loader, meta.transport);
    meta.transport = null;
    meta.loader = null;
    if (!sent) log('Could not send the restart signal.', 'warning');

    // If the port survived the reset, the boot output arrives on it.
    if (meta.gone || !meta.port) return;
    let heard = { boot: null, banner: false };
    try {
      heard = await listenForBoot(meta.port, BOOT_LISTEN_MS);
    } catch (e) {
      // Port re-enumerated; the new port's slot will listen instead.
    }
    if (!awaitingBootRef.current.has(id)) return;
    if (heard.boot) {
      if (heard.boot.mac !== meta.mac) log(`Unexpected boot line from ${heard.boot.mac}`, 'warning');
      matchRestart(heard);
    } else if (heard.banner && !pinned.bootLine) {
      // Same port, so this banner can only be this goggle.
      settleRestart(id, 'restarted');
    }
  }, [patchSlot, slotLog, failSlot, settleRestart, matchRestart]);

  // Full pipeline for one plug-in: (listen for a restarting goggle) ->
  // detect -> erase -> flash -> restart -> done.
  const runSlot = useCallback(async (id, port) => {
    const meta = slotMetaRef.current.get(id);
    const log = slotLog(id);

    // While any flashed goggle is rebooting, a new port may be that goggle
    // coming back. Listen before touching it, so it isn't flashed again.
    if (awaitingBootRef.current.size > 0) {
      patchSlot(id, { state: 'checking' });
      let heard = { boot: null, banner: false };
      try {
        heard = await listenForBoot(port, BOOT_LISTEN_MS);
      } catch (e) {
        // Couldn't open passively; treat as a new goggle.
      }
      const ownerId = matchRestart(heard);
      if (ownerId) {
        adoptPort(id, ownerId, port);
        return;
      }
      await closePort(port);
      if (meta.gone) { removeSlot(id); return; }
    }

    try {
      patchSlot(id, { state: 'detecting' });
      const device = await connectAndDetect(port, log);
      Object.assign(meta, { transport: device.transport, loader: device.loader, mac: device.mac, variant: device.variant });
      patchSlot(id, { mac: device.mac, variant: device.variant, chip: device.chip });

      // Safety net: a restarting goggle whose boot output was missed. Don't
      // flash it again; connecting put it back in download mode, so leave it
      // there (rebooting again would just repeat this) and finish its card.
      const restarting = [...awaitingBootRef.current].find(([, w]) => device.mac && w.mac === device.mac);
      if (restarting) {
        const [ownerId] = restarting;
        slotLog(ownerId)('Came back after restarting; left in download mode.', 'warning');
        settleRestart(ownerId, 'flashed');
        adoptPort(id, ownerId, port, device.transport);
        return;
      }

      log(`Detected ${device.variant ? VARIANT_LABELS[device.variant] : device.chip}`, 'info');

      if (!device.variant) {
        log(`${device.chip} is not a REKKIE goggle. Skipped.`, 'warning');
        finishSlot(id, { state: 'skipped', skipReason: 'Not a REKKIE goggle' });
        return;
      }
      const pinned = pinnedRef.current[device.variant];
      if (!pinned) {
        log(`${VARIANT_SHORT_LABELS[device.variant]} is not selected. Skipped.`, 'info');
        finishSlot(id, { state: 'skipped', skipReason: 'Variant not selected' });
        return;
      }

      trackOTAFlashStarted({ channel: pinned.channel, firmwareVersion: pinned.version, deviceMac: device.mac });
      log(`Flashing ${pinned.version}`, 'info');
      let lastPercent = -1;
      await flashImages(device.loader, pinned.images, {
        log,
        onStage: (stage, imageName) => patchSlot(id, { state: stage, imageName: imageName || null }),
        onProgress: (percent) => {
          const rounded = Math.floor(percent);
          if (rounded === lastPercent) return;
          lastPercent = rounded;
          patchSlot(id, { progress: rounded });
        }
      });

      if (device.mac) {
        await restartSlot(id);
      } else {
        // Without a MAC a restarted goggle can't be told apart from a new
        // one, so leave it in download mode with the port held open.
        completeSlot(id, 'flashed');
      }
    } catch (error) {
      const stage = slotMetaRef.current.get(id)?.state;
      failSlot(id, meta.gone ? `Unplugged during ${stage}` : error.message);
    }
  }, [slotLog, patchSlot, matchRestart, adoptPort, removeSlot, settleRestart, finishSlot, restartSlot, completeSlot, failSlot]);

  const handlePortConnect = useCallback((port) => {
    if (phaseRef.current !== 'running') return;
    const existing = portToSlotRef.current.get(port);
    if (existing && slotMetaRef.current.has(existing) && !slotMetaRef.current.get(existing).gone) return;

    const id = `slot-${nextSlotNumberRef.current}`;
    const label = `Port ${nextSlotNumberRef.current}`;
    nextSlotNumberRef.current += 1;
    slotMetaRef.current.set(id, { port, transport: null, loader: null, gone: false, state: 'checking', mac: null, variant: null, label, removeTimer: null });
    portToSlotRef.current.set(port, id);
    setSlots(prev => [...prev, { id, label, state: 'checking', mac: null, variant: null, progress: 0, startedAt: Date.now() }]);
    runSlot(id, port);
  }, [runSlot]);

  const handlePortDisconnect = useCallback((port) => {
    const id = portToSlotRef.current.get(port);
    if (!id) return;
    portToSlotRef.current.delete(port);
    const meta = slotMetaRef.current.get(id);
    if (!meta || meta.port !== port) return;
    meta.gone = true;
    meta.port = null;
    // A restarting goggle is expected to drop off as it reboots; its card
    // waits for it to come back. In-progress cards are finished by their pipeline.
    const waiting = awaitingBootRef.current.get(id);
    if (waiting && waiting.droppedAt === null) waiting.droppedAt = Date.now();
    if (!IN_PROGRESS_STATES.has(meta.state)) scheduleRemoval(id);
  }, [scheduleRemoval]);

  const { addPortManually } = useSerialPortWatcher({
    active: phase === 'running' || phase === 'stopping',
    onConnect: handlePortConnect,
    onDisconnect: handlePortDisconnect
  });

  // --- Start / Stop ------------------------------------------------------

  const releaseAll = useCallback(async () => {
    awaitingBootRef.current.forEach(w => clearTimeout(w.timer));
    awaitingBootRef.current.clear();
    const metas = [...slotMetaRef.current.values()];
    slotMetaRef.current.clear();
    portToSlotRef.current.clear();
    for (const meta of metas) {
      if (meta.removeTimer) clearTimeout(meta.removeTimer);
      if (meta.transport) await closeTransport(meta.transport);
      else if (meta.port) await closePort(meta.port);
    }
  }, []);

  const handleStart = async () => {
    if (!canStart) return;
    setPhase('preparing');
    try {
      const pinned = {};
      for (const variant of enabledVariants) {
        const { channel, manifest } = rows[variant];
        const images = await loadFirmwareImages(variant, manifest, addLog);
        pinned[variant] = {
          channel,
          manifest,
          version: formatVersion(manifest.manifestFirmware),
          images,
          bootLine: supportsBootLine(variant, manifest.manifestFirmware)
        };
      }
      pinnedRef.current = pinned;
      setCounts({ flashed: 0, errors: 0 });
      setSlots([]);
      setHiddenAt(null);
      nextSlotNumberRef.current = 1;
      setRunStartedAt(new Date());
      const summary = enabledVariants.map(v => `${VARIANT_SHORT_LABELS[v]} → ${pinned[v].channel} ${pinned[v].version}`).join(', ');
      addLog(`Factory flash started: ${summary}`, 'success');
      setPhase('running');
    } catch (error) {
      addLog(`Could not start: ${error.message}`, 'error');
      addError('Could not start factory flash', error.message, 'Firmware');
      setPhase('setup');
    }
  };

  const handleStop = () => {
    addLog('Stopping. Waiting for goggles in progress to finish...', 'info');
    setPhase('stopping');
  };

  // Finish stopping once nothing is mid-flash.
  const stopFinishingRef = useRef(false);
  useEffect(() => {
    if (phase !== 'stopping' || stopFinishingRef.current) return;
    if (slots.some(s => IN_PROGRESS_STATES.has(s.state))) return;
    stopFinishingRef.current = true;
    (async () => {
      await releaseAll();
      setSlots([]);
      pinnedRef.current = {};
      addLog('Factory flash stopped', 'info');
      stopFinishingRef.current = false;
      setPhase('setup');
    })();
  }, [phase, slots, releaseAll, addLog]);

  // Release every port if the page unmounts mid-run.
  useEffect(() => () => { releaseAll(); }, [releaseAll]);

  // --- Foreground tab ----------------------------------------------------

  useEffect(() => {
    if (phase !== 'running' && phase !== 'stopping') return;
    const acquire = async () => {
      try {
        if ('wakeLock' in navigator && document.visibilityState === 'visible') {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch (e) {
        // Wake lock refused (battery saver, policy); flashing still works.
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        setHiddenAt(new Date());
        addLog('Tab hidden while flashing. Chrome slows hidden tabs, which can stall or fail flashes.', 'warning');
      } else {
        acquire();
      }
    };
    acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [phase, addLog]);

  // --- Render ------------------------------------------------------------

  if (phase === 'setup' || phase === 'preparing') {
    return (
      <div className="ota-section factory-setup">
        <h3>Factory Flash Setup</h3>
        <p className="section-note">Pick the firmware to flash for each goggle model. Unselected models are skipped.</p>

        <div className="factory-variant-table">
          <div className="factory-variant-row factory-variant-head">
            <span>Model</span><span>Flash</span><span>Channel</span><span>Version</span>
          </div>
          {VARIANTS.map(variant => {
            const row = rows[variant];
            const disabled = phase === 'preparing';
            return (
              <div key={variant} className={`factory-variant-row ${row.enabled ? '' : 'is-off'}`}>
                <span className="factory-variant-name">
                  {VARIANT_SHORT_LABELS[variant]}
                  <span className="factory-variant-slug">{variant}</span>
                </span>
                <span>
                  <input
                    type="checkbox"
                    id={`factory-enable-${variant}`}
                    aria-label={`Flash ${VARIANT_LABELS[variant]}`}
                    checked={row.enabled}
                    disabled={disabled}
                    onChange={(e) => updateRow(variant, { enabled: e.target.checked })}
                  />
                </span>
                <span>
                  <select
                    id={`factory-channel-${variant}`}
                    className="factory-select"
                    value={row.channel}
                    disabled={disabled || !row.enabled || isLoadingChannels}
                    onChange={(e) => selectChannel(variant, e.target.value)}
                  >
                    <option value="">{isLoadingChannels ? 'Loading…' : 'Select channel'}</option>
                    {channels.map(ch => <option key={ch} value={ch}>{formatChannelName(ch)}</option>)}
                  </select>
                </span>
                <span>
                  <select
                    id={`factory-version-${variant}`}
                    className="factory-select"
                    value={row.manifest ? formatVersion(row.manifest.manifestFirmware) : ''}
                    disabled={disabled || !row.enabled || !row.channel || row.loading || row.manifests.length === 0}
                    onChange={(e) => updateRow(variant, { manifest: row.manifests.find(m => formatVersion(m.manifestFirmware) === e.target.value) || null })}
                  >
                    {row.loading && <option value="">Loading…</option>}
                    {!row.loading && row.channel && row.manifests.length === 0 && <option value="">No versions</option>}
                    {!row.loading && !row.channel && <option value="">—</option>}
                    {row.manifests.map((m, i) => {
                      const v = formatVersion(m.manifestFirmware);
                      return <option key={v} value={v}>{v}{i === 0 ? ' (Latest)' : ''}</option>;
                    })}
                  </select>
                </span>
              </div>
            );
          })}
        </div>

        <div className="factory-setup-actions">
          <Button variant="primary" size="large" onClick={handleStart} disabled={!canStart}>
            {phase === 'preparing' ? 'Downloading firmware…' : 'Start'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="factory-run">
      <div className="ota-section factory-run-header">
        <div className="factory-run-status">
          <span className="status-indicator connected"></span>
          <strong>{phase === 'stopping' ? 'Stopping…' : 'Running'}</strong>
          {runStartedAt && <span className="factory-muted">since {formatClock(runStartedAt)}</span>}
          <span className="factory-count">Flashed <strong>{counts.flashed}</strong></span>
          <span className={`factory-count ${counts.errors ? 'has-errors' : ''}`}>Errors <strong>{counts.errors}</strong></span>
        </div>
        <div className="factory-run-actions">
          <Button variant="outline" size="small" onClick={addPortManually} disabled={phase !== 'running'}>
            Add goggle
          </Button>
          <Button variant="danger" size="small" onClick={handleStop} disabled={phase !== 'running'}>
            Stop
          </Button>
        </div>
        <div className="factory-pins">
          {VARIANTS.map(v => {
            const pinned = pinnedRef.current[v];
            return (
              <span key={v} className={`factory-pin ${pinned ? '' : 'is-off'}`}>
                {VARIANT_SHORT_LABELS[v]} → {pinned ? `${formatChannelName(pinned.channel)} ${pinned.version}` : 'off'}
              </span>
            );
          })}
        </div>
      </div>

      {hiddenAt && (
        <div className="factory-hidden-warning" role="alert">
          <span>This tab was hidden at {formatClock(hiddenAt)}. Keep it visible while flashing; Chrome slows hidden tabs and flashes can stall or fail.</span>
          <Button variant="outline" size="small" onClick={() => setHiddenAt(null)}>Dismiss</Button>
        </div>
      )}

      {slots.length === 0 ? (
        <div className="factory-empty">Plug in goggles to start flashing.</div>
      ) : (
        <div className="factory-cards">
          {slots.map(slot => <SlotCard key={slot.id} slot={slot} />)}
        </div>
      )}
    </div>
  );
};

const STATE_LABELS = {
  checking: 'Checking',
  detecting: 'Detecting',
  erasing: 'Erasing',
  restarting: 'Restarting',
  skipped: 'Skipped',
  error: 'Error · replug to retry'
};

const SlotCard = ({ slot }) => {
  const { state } = slot;
  let title = STATE_LABELS[state] || state;
  if (state === 'flashing') title = `Flashing ${slot.imageName ? slot.imageName.toLowerCase() : ''}`.trim();
  if (state === 'done') title = slot.outcome === 'verified' ? 'Verified · unplug' : slot.outcome === 'restarted' ? 'Restarted · unplug' : 'Done · unplug';

  const tone = state === 'done' ? 'ok'
    : state === 'error' ? 'bad'
    : state === 'skipped' ? 'skip'
    : 'run';
  const showBar = state === 'erasing' || state === 'flashing' || state === 'restarting' || state === 'detecting' || state === 'checking';
  const indeterminate = state !== 'flashing';
  const seconds = slot.finishedAt ? Math.round((slot.finishedAt - slot.startedAt) / 1000) : null;

  return (
    <div className={`factory-card tone-${tone}`}>
      <div className="factory-card-head">
        <span className="factory-card-variant">{slot.variant ? VARIANT_SHORT_LABELS[slot.variant] : slot.chip || '—'}</span>
        <span className="factory-card-mac">{slot.mac || slot.label}</span>
      </div>
      <div className="factory-card-state">{title}</div>
      {showBar && (
        <div className={`factory-bar ${indeterminate ? 'is-indeterminate' : ''}`}>
          <i style={indeterminate ? {} : { width: `${slot.progress || 0}%` }} />
        </div>
      )}
      {state === 'flashing' && <div className="factory-card-sub">{slot.progress || 0}%</div>}
      {state === 'skipped' && <div className="factory-card-sub">{slot.skipReason}</div>}
      {state === 'error' && <div className="factory-card-sub">{slot.error}</div>}
      {state === 'done' && seconds !== null && <div className="factory-card-sub">{seconds} s</div>}
    </div>
  );
};

export default FactoryFlashMode;
